import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import {
  getApplicationFlow as clientGetApplicationFlow,
  submitApplicationFlow as clientSubmitApplicationFlow,
} from 'typescript/clients/capital_shop/application_flow_client';
import { MUTATION_KEYS, QUERY_KEYS } from 'typescript/constants/react_query';
import { SubmitApplicationFlowRequestData } from 'typescript/types/api/capital_shop/application_flows';

const useGetApplicationFlow = ({
  applicationFlowToken,
  enabled = true,
  refetchInterval,
}: {
  applicationFlowToken: string;
  enabled?: boolean;
  refetchInterval?: number;
}) => {
  const { data: applicationFlowData, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.APPLICATION_FLOWS, { applicationFlowToken }],
    queryFn: () =>
      clientGetApplicationFlow({
        applicationFlowToken,
      }),
    enabled,
    ...(refetchInterval !== undefined && { refetchInterval }),
  });

  return { applicationFlowData, ...rest };
};

const useSubmitApplicationFlow = () => {
  const queryClient = useQueryClient();

  const { mutate: submitApplicationFlow, ...rest } = useMutation({
    mutationKey: [MUTATION_KEYS.SUBMIT_APPLICATION_FLOW],
    mutationFn: ({
      applicationFlowToken,
      applicationFlowData,
    }: {
      applicationFlowToken: string;
      applicationFlowData: SubmitApplicationFlowRequestData;
    }) =>
      clientSubmitApplicationFlow({
        applicationFlowToken,
        applicationFlowData,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.APPLICATION_FLOWS,
          { applicationFlowToken: variables.applicationFlowToken },
        ],
      });
    },
    onError: (error, variables) =>
      captureException(
        new CustomError(MUTATION_KEYS.SUBMIT_APPLICATION_FLOW, error),
        {
          extra: {
            applicationFlowToken: variables.applicationFlowToken,
          },
        },
      ),
  });

  return { submitApplicationFlow, ...rest };
};

export { useGetApplicationFlow, useSubmitApplicationFlow };
